import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getCertificate, verifyCertificate } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Download, Printer, Award, CheckCircle, XCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lvc-live-classes/artifacts/op54e7uq_WhatsApp%20Image%202026-04-08%20at%2013.06.26.jpeg";

export default function CertificatePage() {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const certificateRef = useRef(null);

  const verifyMode = searchParams.get('verify');

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        let data;
        if (verifyMode) {
          // Public verification by certificate number
          data = await verifyCertificate(certificateId);
          if (!data.valid) {
            setError('Certificado no encontrado');
            setLoading(false);
            return;
          }
          setCertificate({
            certificate_number: data.certificate_number,
            user_name: data.student_name,
            course_title: data.course_title,
            issued_at: data.issued_at
          });
        } else {
          // Private view by certificate ID
          data = await getCertificate(certificateId);
          setCertificate(data);
        }
      } catch (err) {
        setError('Error al cargar el certificado');
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [certificateId, verifyMode]);

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 mx-auto text-[#9B2226] mb-4" />
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-2">{error}</h1>
          <p className="text-[#737373] mb-6">El certificado no existe o no tienes acceso</p>
          <Button onClick={() => navigate('/')} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
            Volver al Inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Controls - Hidden when printing */}
      <div className="print:hidden bg-[#FDFBF7] border-b border-[#E5DFD3] p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#4A4A4A] hover:text-[#1A1A1A]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handlePrint}
              className="border-[#8B1538] text-[#8B1538] hover:bg-[#8B1538]/5"
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-[#8B1538] hover:bg-[#70102B] text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Certificate */}
      <div className="p-8 print:p-0">
        <div 
          ref={certificateRef}
          className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none"
          style={{ aspectRatio: '1.414/1' }}
        >
          {/* Certificate Content */}
          <div className="h-full p-12 flex flex-col relative overflow-hidden">
            {/* Decorative Border */}
            <div className="absolute inset-4 border-4 border-[#8B1538]/20 rounded-lg pointer-events-none" />
            <div className="absolute inset-6 border border-[#D9B48F]/50 rounded-lg pointer-events-none" />
            
            {/* Corner Decorations */}
            <div className="absolute top-8 left-8 w-16 h-16 border-l-4 border-t-4 border-[#8B1538] rounded-tl-lg" />
            <div className="absolute top-8 right-8 w-16 h-16 border-r-4 border-t-4 border-[#8B1538] rounded-tr-lg" />
            <div className="absolute bottom-8 left-8 w-16 h-16 border-l-4 border-b-4 border-[#8B1538] rounded-bl-lg" />
            <div className="absolute bottom-8 right-8 w-16 h-16 border-r-4 border-b-4 border-[#8B1538] rounded-br-lg" />

            {/* Header */}
            <div className="text-center mb-8 relative z-10">
              <img 
                src={LOGO_URL} 
                alt="LVC Misiones" 
                className="h-20 w-20 mx-auto mb-4 rounded-full object-cover"
              />
              <h1 className="font-heading text-3xl text-[#8B1538] tracking-wide">
                LA VOZ DE CRISTO PARA LAS NACIONES
              </h1>
              <p className="text-sm text-[#737373] tracking-[0.3em] mt-2">FORMACIÓN MISIONERA</p>
            </div>

            {/* Certificate Title */}
            <div className="text-center mb-8 relative z-10">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="h-px w-20 bg-gradient-to-r from-transparent to-[#D9B48F]" />
                <Award className="h-8 w-8 text-[#D9B48F]" />
                <div className="h-px w-20 bg-gradient-to-l from-transparent to-[#D9B48F]" />
              </div>
              <h2 className="font-heading text-5xl text-[#1A1A1A] tracking-tight">
                CERTIFICADO
              </h2>
              <p className="text-lg text-[#737373] mt-2">de Finalización</p>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center text-center relative z-10">
              <p className="text-lg text-[#4A4A4A] mb-4">Se otorga el presente certificado a</p>
              
              <h3 className="font-heading text-4xl text-[#8B1538] mb-6 border-b-2 border-[#D9B48F] pb-2 px-8">
                {certificate.user_name}
              </h3>
              
              <p className="text-lg text-[#4A4A4A] mb-4">
                por haber completado satisfactoriamente el programa
              </p>
              
              <h4 className="font-heading text-2xl text-[#1A1A1A] mb-6 max-w-lg">
                "{certificate.course_title}"
              </h4>
              
              <p className="text-[#737373]">
                Con una duración de {certificate.duration_months || 6} meses de formación intensiva
              </p>
            </div>

            {/* Footer */}
            <div className="mt-auto relative z-10">
              <div className="flex justify-between items-end">
                <div className="text-center">
                  <div className="w-48 border-t border-[#4A4A4A] pt-2">
                    <p className="text-sm text-[#4A4A4A]">Director</p>
                    <p className="text-xs text-[#737373]">LVC Misiones</p>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-sm text-[#737373] mb-1">Fecha de emisión</p>
                  <p className="font-medium text-[#1A1A1A]">{formatDate(certificate.issued_at)}</p>
                  <p className="text-xs text-[#737373] mt-2">
                    Certificado N° {certificate.certificate_number}
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-48 border-t border-[#4A4A4A] pt-2">
                    <p className="text-sm text-[#4A4A4A]">Coordinador Académico</p>
                    <p className="text-xs text-[#737373]">LVC Misiones</p>
                  </div>
                </div>
              </div>
              
              {/* Verification Note */}
              <p className="text-center text-xs text-[#737373] mt-6">
                Verificar autenticidad en: lvc-live-classes.preview.emergentagent.com/certificate/{certificate.certificate_number}?verify=true
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Verification Badge - Only in verify mode */}
      {verifyMode && (
        <div className="print:hidden max-w-4xl mx-auto px-8 pb-8">
          <div className="bg-[#2D6A4F]/10 border border-[#2D6A4F]/30 rounded-lg p-4 flex items-center gap-4">
            <CheckCircle className="h-8 w-8 text-[#2D6A4F]" />
            <div>
              <h4 className="font-medium text-[#2D6A4F]">Certificado Verificado</h4>
              <p className="text-sm text-[#4A4A4A]">
                Este certificado es auténtico y fue emitido por La Voz de Cristo Para Las Naciones.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 landscape; margin: 0; }
        }
      `}</style>
    </div>
  );
}
